import { ArrowLeftRight, HandCoins, Wallet } from "lucide-react"
import * as React from "react"
import { Link } from "react-router-dom"
import { toast } from "sonner"

import { PageHeader } from "@/components/layout"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { ApiError, api } from "@/lib/api"
import type { AdminDebtRow } from "@/lib/types"

const ALL = "__all__"

const money = (value: number) =>
  value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const DIRECTION_LABEL: Record<AdminDebtRow["direction"], string> = {
  they_owe_me: "Owed to user",
  i_owe: "User owes",
}

function StatCard({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  value: string | number
}) {
  return (
    <Card>
      <CardContent className="flex items-start gap-3 p-4">
        <div className="bg-muted text-muted-foreground rounded-md p-2">
          <Icon className="size-4" />
        </div>
        <div className="min-w-0">
          <div className="text-muted-foreground text-xs font-medium">{label}</div>
          <div className="text-2xl font-semibold leading-tight">{value}</div>
        </div>
      </CardContent>
    </Card>
  )
}

export function AdminDebtsPage() {
  const [rows, setRows] = React.useState<AdminDebtRow[]>([])
  const [loading, setLoading] = React.useState(true)
  const [direction, setDirection] = React.useState(ALL)

  React.useEffect(() => {
    setLoading(true)
    api
      .adminDebts(200, 0)
      .then((page) => setRows(page.items))
      .catch((error) =>
        toast.error(error instanceof ApiError ? error.message : "Could not load debts."),
      )
      .finally(() => setLoading(false))
  }, [])

  const visible = direction === ALL ? rows : rows.filter((r) => r.direction === direction)
  const outstanding = visible
    .filter((r) => !r.settled)
    .reduce((sum, r) => sum + r.remaining, 0)

  return (
    <>
      <PageHeader
        title="Debts"
        description="Every debt tracked by users through the Expense Tracker plugin."
        action={
          <div className="flex items-center gap-2">
            <Select value={direction} onValueChange={setDirection}>
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All directions</SelectItem>
                <SelectItem value="they_owe_me">Owed to user</SelectItem>
                <SelectItem value="i_owe">User owes</SelectItem>
              </SelectContent>
            </Select>
            <Button asChild variant="outline">
              <Link to="/admin">Back to admin</Link>
            </Button>
          </div>
        }
      />
      <div className="space-y-6 p-6">
        {loading ? (
          <Skeleton className="h-64 w-full" />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <StatCard
                icon={HandCoins}
                label="Debts"
                value={visible.length.toLocaleString()}
              />
              <StatCard icon={Wallet} label="Outstanding (unsettled)" value={money(outstanding)} />
            </div>
            {rows.length >= 200 && (
              <p className="text-muted-foreground text-xs">Showing the 200 most recent debts.</p>
            )}
            {visible.length === 0 ? (
              <p className="text-muted-foreground text-sm">No debts recorded.</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium">Owner</th>
                      <th className="px-4 py-2 text-left font-medium">Counterparty</th>
                      <th className="px-4 py-2 text-left font-medium">Direction</th>
                      <th className="px-4 py-2 text-right font-medium">Amount</th>
                      <th className="px-4 py-2 text-right font-medium">Paid</th>
                      <th className="px-4 py-2 text-right font-medium">Remaining</th>
                      <th className="px-4 py-2 text-left font-medium">Due</th>
                      <th className="px-4 py-2 text-left font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((row) => (
                      <tr key={row.id} className="border-t">
                        <td className="px-4 py-2">{row.owner_email}</td>
                        <td className="px-4 py-2">{row.counterparty}</td>
                        <td className="px-4 py-2">
                          <span className="inline-flex items-center gap-1">
                            <ArrowLeftRight className="text-muted-foreground size-3" />
                            {DIRECTION_LABEL[row.direction]}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">
                          {money(row.amount)} {row.currency}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">{money(row.paid)}</td>
                        <td className="px-4 py-2 text-right tabular-nums font-medium">
                          {money(row.remaining)}
                        </td>
                        <td className="text-muted-foreground px-4 py-2 tabular-nums">
                          {row.due_date ?? "—"}
                        </td>
                        <td className="px-4 py-2">
                          {row.settled ? (
                            <Badge variant="secondary">Settled</Badge>
                          ) : (
                            <Badge>Open</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="text-muted-foreground text-xs">Remaining = amount − repayments.</p>
          </>
        )}
      </div>
    </>
  )
}
